import type { Coordinates3D, IsoDate, IsoDateTime } from './common';
import type { LocationId, WarehouseId } from './ids';
import type { ReceivingPolicy } from './inbound';
import type { RackGeometry } from './layout';
import type { PutawayPolicy } from './putaway';

export const LOCATION_TYPES = [
  'receiving',
  'pick',
  'bulk',
  'staging',
  'packing',
  'shipping',
  'returns',
  'quarantine',
  'damaged',
] as const;
export type LocationType = (typeof LOCATION_TYPES)[number];

/** Stock in these location types counts as kullanılabilir and may be allocated to orders. */
export const SELLABLE_LOCATION_TYPES = ['pick', 'bulk'] as const satisfies readonly LocationType[];

/** Cell address: Koridor → Raf → Kat → Göz. */
export interface LocationAddress {
  /** Koridor: 1–3 letters A–Z */
  readonly aisle: string;
  /** Raf (bay along the aisle): 1–99 */
  readonly rack: number;
  /** Kat (shelf level, 1 = floor): 1–99 */
  readonly level: number;
  /** Göz (position on the shelf): 1–99 */
  readonly bin: number;
}

/** "A-03-2-1": aisle, rack (2 digits), level, bin. */
export type LocationCode = string;

/** Routing hints stored in warehouses.layout. */
export interface WarehouseLayout {
  /** Axis the aisles run along; racks are numbered along it. Default 'y'. */
  readonly aisleAxis?: 'x' | 'y';
  /** Cross-aisle positions on the aisle axis where a picker can change aisle, front first. */
  readonly crossAisles?: readonly number[];
  /** Where pick routes start and end, usually the packing area or the office door. */
  readonly depot?: Coordinates3D;
  /** Buildings and rooms on the floor plan that hold no stock (office, canteen): drawn on the map. */
  readonly buildings?: readonly FloorBuilding[];
}

/** A building or room standing on the warehouse floor (the office), at its real size. */
export interface FloorBuilding {
  readonly id: string;
  readonly name: string;
  /** centre of the footprint, metres */
  readonly x: number;
  readonly y: number;
  /** along x, metres */
  readonly width: number;
  /** along y, metres */
  readonly depth: number;
  readonly height: number;
}

/** One bin on the floor plan of the warehouse map. */
export interface MapBin {
  readonly code: string;
  readonly aisle: string;
  readonly rack: number;
  readonly level: number;
  readonly bin: number;
  readonly type: LocationType;
  readonly at: Coordinates3D;
  readonly isBlocked: boolean;
  /** volume the bin holds, cm³; null when not set */
  readonly maxVolumeCm3: number | null;
}

/** Everything the warehouse map draws: the layout, every bin that has floor coordinates and the racks holding them. */
export interface WarehouseMap {
  readonly warehouseId: string;
  readonly layout: WarehouseLayout;
  readonly bins: readonly MapBin[];
  /** size and place of every rack, pallet place and area with active bins */
  readonly racks: readonly RackGeometry[];
}

/** Stock lying in one bin, for the map's colours. Empty bins are left out. */
export interface MapBinStock {
  readonly code: LocationCode;
  /** different products in the bin */
  readonly skuCount: number;
  readonly onHand: number;
  readonly reserved: number;
  /** cm³ the stock takes up; products without dimensions count as 0 */
  readonly usedVolumeCm3: number;
  readonly maxVolumeCm3: number | null;
  /** sellers with stock in the bin, by code */
  readonly tenants: readonly string[];
  /** earliest son kullanma tarihi in the bin */
  readonly nearestExpiry: IsoDate | null;
}

/** One stock line of a bin: product, lot and expiry. */
export interface MapBinItem {
  readonly productId: string;
  readonly sku: string;
  readonly name: string;
  readonly imageUrl: string | null;
  readonly tenantCode: string;
  readonly lotNo: string | null;
  readonly expiryDate: IsoDate | null;
  readonly onHand: number;
  /** promised to orders: still to be picked, or picked and waiting to be packed */
  readonly reserved: number;
  /** picked into a tote, not packed yet: part of onHand and reserved, no longer on the shelf */
  readonly picked: number;
}

/** A bin opened on the map: its capacity and what lies in it. */
export interface MapBinDetail {
  readonly code: LocationCode;
  readonly type: LocationType;
  readonly isBlocked: boolean;
  readonly maxVolumeCm3: number | null;
  readonly usedVolumeCm3: number;
  readonly items: readonly MapBinItem[];
}

/** Operating rules stored in warehouses.settings; missing keys fall back to the defaults. */
export interface WarehouseSettings {
  readonly putaway?: Partial<PutawayPolicy>;
  readonly receiving?: Partial<ReceivingPolicy>;
}

export interface Warehouse {
  readonly id: WarehouseId;
  readonly code: string;
  readonly name: string;
  readonly timezone: string;
  readonly layout: WarehouseLayout;
  readonly settings: WarehouseSettings;
  readonly isActive: boolean;
}

export interface WarehouseLocation extends LocationAddress {
  readonly id: LocationId;
  readonly warehouseId: WarehouseId;
  /** "A-03-2-1" */
  readonly code: LocationCode;
  /** "LA030201": A–Z/0–9 only, so keyboard-wedge scanners on Turkish-Q layouts read it intact. */
  readonly barcode: string;
  readonly type: LocationType;
  readonly zone: string | null;
  /** Pick-face position for routing and the future 2D/3D map; null until the floor plan is drawn. */
  readonly coordinates: Coordinates3D | null;
  /** Manual walking order, used when coordinates are missing. */
  readonly pickSequence: number | null;
  readonly maxWeightG: number | null;
  readonly maxVolumeCm3: number | null;
  readonly isActive: boolean;
  /** Temporarily not pickable (cycle count, damage); its stock stays on the books. */
  readonly isBlocked: boolean;
  readonly createdAt: IsoDateTime;
  readonly updatedAt: IsoDateTime;
}

const pad2 = (n: number): string => String(n).padStart(2, '0');

/** Mirrors the generated column warehouse_locations.code. */
export function formatLocationCode(a: LocationAddress): LocationCode {
  return `${a.aisle}-${pad2(a.rack)}-${a.level}-${a.bin}`;
}

/** Mirrors the generated column warehouse_locations.barcode. */
export function locationBarcode(a: LocationAddress): string {
  return `L${a.aisle}${pad2(a.rack)}${pad2(a.level)}${pad2(a.bin)}`;
}
